import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { LocationSweepstakeDetailsHeader } from '../LocationSweepstakesDetailsPage/LocationSweepstakeDetailsHeader'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetSweepstakeByIdWithGroupDrawingInfo } from 'hooks/api/useGetSweepstakeByIdWithGroupDrawingInfo'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { isNil } from 'lodash'
import { DataTable } from 'components/Shared/DataTable'
import noLocationFound from 'assets/locationNotFound.svg'
import { type GridRowParams } from '@mui/x-data-grid'
import { useState, type ElementType } from 'react'
import { NoResultsOverlayCustom } from 'components/DataTable/NoResultsOverlayCustom'
import { type SweepstakeDrawing } from 'types/api'
import { LocationSweepstakesRequestRedrawModal } from './LocationSweepstakesRequestRedrawModal'
import { useRedrawWinner } from 'hooks/api/Sweepstake/useRedrawWinner'
import { useSnackbar } from 'stores/useSnackbar'
import {
  getLocationWinnersColumns,
  getLocationWinnersCAOrOrgColumns,
  type LocationWinnerTableProps,
} from './LocationWinnersColumns'
import { type components } from 'api/playerPayback/api'
import { useRequestRedrawModal } from 'stores/useRequestRedrawModal'
import { AdminExportModal } from 'pages/AdminExport/AdminExportModal'
import ExportIcon from 'assets/export.svg'
import { LookupType } from 'utils/adminExportUtils'
import { useNotifyLocations } from 'hooks/api/Sweepstake/useNotifyLocations'
import { LocationSweepstakesConfirmationModal } from './LocationSweepstakesConfirmationModal'

const resolveIconComponent = (iconSource: unknown): ElementType => {
  if (iconSource && typeof iconSource === 'object' && 'default' in iconSource) {
    return (iconSource as { default: ElementType }).default
  }

  return iconSource as ElementType
}

export interface SelectedWinnerType {
  locationName: string
  licenseNumber: string
  winnerName: string
  sweepstakesWinner: components['schemas']['FullSweepstakeWinnerDTO']
  drawingSequence: number
  selectedWinnerId: number
}

export const LocationSweepstakesWinnersPage = () => {
  const ExpandMoreIconComponent = resolveIconComponent(ExpandMoreIcon)
  const { id: idParam } = useParams()
  const navigate = useNavigate()
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)
  const sweepstakeQuery = useGetSweepstakeByIdWithGroupDrawingInfo(
    Number(idParam)
  )
  // Mutations
  const redrawWinnerMutation = useRedrawWinner({
    invalidateQueryKeys: [
      ['/sweepstakes/drawing-info/multi-draw', Number(idParam)],
    ],
    onSuccess: () => {
      setSnackbarMessage('Winner redrawn successfully', 'success')
    },
    onError: () => {
      setSnackbarMessage('Error redrawing winner', 'error')
    },
  })
  const notifyLocationsMutation = useNotifyLocations()
  // Notify Winners Modal
  const [isNotifyWinnersModalOpen, setIsNotifyWinnersModalOpen] =
    useState(false)
  const toggleNotifyWinnersModalIsOpen = () =>
    setIsNotifyWinnersModalOpen(!isNotifyWinnersModalOpen)

  // Request New Winners Modal
  const { setData: setIsIndividualRedrawModalOpen } = useRequestRedrawModal()

  const [isAdminExportModalOpen, setIsAdminExportModalOpen] = useState(false)
  const toggleAdminExportModal = () =>
    setIsAdminExportModalOpen(!isAdminExportModalOpen)

  const [lookupType, setLookupType] = useState<LookupType | undefined>(
    undefined
  )

  const handleExportClick = () => {
    setLookupType(LookupType.WinnersByContestId)
    toggleAdminExportModal()
  }

  const handleRowClick = (params: GridRowParams) => {
    const clickedRow = params.row
    if (clickedRow.winnerName !== '-') {
      navigate(`/Winners/${params.id}`)
    }
  }

  const notifyLocations = () => {
    notifyLocationsMutation.mutate({ contestId: sweepstake.id })
  }

  const redrawWinners = (
    theReason: string,
    sweepstakeDrawingId: number,
    sweepstakeWinnerId?: number
  ) => {
    redrawWinnerMutation.mutate({
      sweepstakeDrawingId,
      redrawReason: theReason,
      sweepstakeWinnerId,
    })
  }

  if (sweepstakeQuery.isPending) {
    return <ActivityIndicator />
  }

  if (sweepstakeQuery.isError || isNil(sweepstakeQuery.data)) {
    return <Box>An error occurred fetching the sweepstake.</Box>
  }

  // just use the first sweepstake in the list for general info such as name, state etc...
  const sweepstake = sweepstakeQuery.data[0]

  const getLocationWinners = (
    drawings: SweepstakeDrawing[]
  ): LocationWinnerTableProps[] => {
    return drawings
      .flatMap((ld) => {
        const currentSweepstake = sweepstakeQuery.data?.find(
          (x) => x.id === ld.contestId
        )

        const realWinners: LocationWinnerTableProps[] =
          ld.sweepstakeWinners && ld.sweepstakeWinners?.length > 0
            ? ld.sweepstakeWinners.map((winner) => {
                const fullName = `${String(winner.patron?.firstName)} ${String(winner.patron?.lastName)}`
                const validWinnerName = fullName.trim() !== ''
                return {
                  id: winner.id,
                  leName:
                    ld?.licensedEstablishment?.standardName ??
                    winner?.contestEntry?.patronCheckIn?.licensedEstablishment
                      ?.standardName ??
                    '???',
                  leLicenseNumber: ld?.licensedEstablishment?.licenseNumber
                    ? `#${String(ld.licensedEstablishment.licenseNumber)}`
                    : winner?.contestEntry?.patronCheckIn?.licensedEstablishment
                          ?.licenseNumber
                      ? `#${String(winner.contestEntry.patronCheckIn.licensedEstablishment.licenseNumber)}`
                      : '???',
                  winnerName: validWinnerName ? fullName : '-',
                  patronId: winner.patronId,
                  winnerStatus: winner.status ?? 'NoWinner',
                  dto: winner,
                  resultType: winner.resultType,
                  drawingSequence:
                    currentSweepstake?.contestWinners &&
                    currentSweepstake?.contestWinners?.length > 0
                      ? currentSweepstake.contestWinners.find(
                          (x) => x.id === winner.contestWinnerId
                        )?.winnerNumber
                      : winner.drawingSequence,
                  drawnDate: winner.dateDrawn ?? '-',
                  notes: winner.notes ?? '',
                  prize:
                    currentSweepstake?.contestWinners &&
                    currentSweepstake.contestWinners.length > 0 &&
                    winner.resultType === 'Winner'
                      ? (currentSweepstake.contestWinners.find(
                          (x) => x.id === winner.contestWinnerId
                        )?.prizeDescription ?? '')
                      : '',
                  contestWinnerId: winner.contestWinnerId,
                }
              })
            : []

        // check if not enough winners for each location and add placeholder 'No Winner' objects
        const placeholdersNeeded =
          Number(currentSweepstake?.numLocationWinners) -
          realWinners.filter((x) => x.resultType === 'Winner').length

        const placeholderWinners: LocationWinnerTableProps[] = Array.from(
          { length: Math.max(0, placeholdersNeeded) },
          (_, i) => ({
            id: Number(ld?.licensedEstablishmentId) * 100 + i, // make sure id is unique
            leName: ld?.licensedEstablishment?.standardName ?? '???',
            leLicenseNumber: ld?.licensedEstablishment?.licenseNumber
              ? `#${String(ld.licensedEstablishment.licenseNumber)}`
              : '???',
            winnerName: '-',
            patronId: -1,
            dto: {
              id: -1,
              sweepstakeDrawingId: -1,
              contestEntryId: -1,
              patronId: -1,
              patronTierId: -1,
            },
            winnerStatus: 'NoWinner',
            resultType: 'NoWinner',
            drawingSequence: Number(sweepstake.numLocationWinners) + i, // put at end of the list
            drawnDate: '-',
            notes: '',
            prize: '',
            contestWinnerId: null,
          })
        )

        return [...realWinners, ...placeholderWinners]
      })

      .filter(
        (winner) =>
          winner.resultType === 'Winner' || winner.winnerStatus === 'NoWinner'
      )
      .sort((a, b) => {
        // if CA or Org sweepstake, just sort by drawing sequence
        if (
          sweepstake.corporateAccountId !== null ||
          sweepstake.organizationId !== null
        ) {
          const aSeq = Number(a.drawingSequence ?? 0)
          const bSeq = Number(b.drawingSequence ?? 0)
          return aSeq - bSeq
          // otherwise, sort by LE name and then drawing sequence
        } else {
          // 1. Sort by leName alphabetically
          const leCompare = a.leName.localeCompare(b.leName)
          if (leCompare !== 0) return leCompare

          // 2. If leName is the same, sort by drawingSequence
          const aSeq = Number(a.drawingSequence ?? 0)
          const bSeq = Number(b.drawingSequence ?? 0)
          return aSeq - bSeq
        }
      })
  }

  const locationWinnersColumns =
    sweepstakeQuery.data &&
    sweepstakeQuery.data[0].corporateAccountId === null &&
    sweepstakeQuery.data[0].organizationId === null
      ? getLocationWinnersColumns(
          [], // we don't allow multi select rows for location sweepstakes so pass empty values
          () => {},
          () => {},
          [],
          null,
          setIsIndividualRedrawModalOpen,
          sweepstake.name ?? ''
        )
      : getLocationWinnersCAOrOrgColumns(
          [], // we don't allow multi select rows for location sweepstakes so pass empty values
          () => {},
          () => {},
          [],
          null,
          setIsIndividualRedrawModalOpen,
          sweepstake.name ?? ''
        )

  return (
    <>
      <LocationSweepstakeDetailsHeader
        sweepstake={sweepstake}
        currentTab={`/Contests/LocationSweepstakes/${String(sweepstake.id)}/Winners`}
        actionButton={
          <Box>
            <Button
              sx={{ marginLeft: '15px' }}
              variant="contained"
              onClick={toggleNotifyWinnersModalIsOpen}
            >
              Notify Locations of Winners
            </Button>
          </Box>
        }
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 3,
            }}
          >
            <Typography variant="h2">Location Winners</Typography>
          </Box>
          {sweepstakeQuery.data?.map((drawing, index) => (
            <Accordion key={drawing.id} defaultExpanded={true}>
              <AccordionSummary
                expandIcon={
                  ExpandMoreIconComponent ? <ExpandMoreIconComponent /> : null
                }
              >
                <Typography variant="h4">
                  {`Drawing ${Number(index) + 1} - ${new Date(
                    String(drawing.startDate)
                  ).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  })} - ${new Date(String(drawing.endDate)).toLocaleDateString(
                    'en-US',
                    {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    }
                  )}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DataTable<LocationWinnerTableProps>
                  onRowClick={handleRowClick}
                  columns={locationWinnersColumns}
                  rows={getLocationWinners(drawing.sweepstakeDrawings ?? [])}
                  emptyDataImageSource={noLocationFound}
                  noDataMessage="After the sweepstake ends and winners are drawn, location winners will be posted here."
                  slots={{ noResultsOverlay: NoResultsOverlayCustom }}
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
        <Button
          variant="contained"
          fullWidth
          sx={{
            marginTop: 5,
          }}
          onClick={handleExportClick}
        >
          <img src={ExportIcon} width={15} height={15} alt="Export icon" />
          <Typography variant="label-CTA">Export</Typography>
        </Button>
        <LocationSweepstakesRequestRedrawModal redrawWinners={redrawWinners} />
        <LocationSweepstakesConfirmationModal
          variant="Notify"
          sweepstakeName={sweepstake.name}
          preferredEmailSet={sweepstake.preferredEmail !== null}
          isCAorORG={
            sweepstake.corporateAccountId !== null ||
            sweepstake.organizationId !== null
          }
          isModalOpen={isNotifyWinnersModalOpen}
          toggleIsOpen={toggleNotifyWinnersModalIsOpen}
          onConfirm={() => {
            toggleNotifyWinnersModalIsOpen()
            notifyLocations()
          }}
          onCancel={toggleNotifyWinnersModalIsOpen}
        />
      </LocationSweepstakeDetailsHeader>
      <AdminExportModal
        isModalOpen={isAdminExportModalOpen}
        toggleModal={toggleAdminExportModal}
        header={sweepstake.name}
        lookupType={lookupType}
        lookupId={idParam}
      />
    </>
  )
}
